/**
 * Centralised glossary of operator-admin terms. Single source of truth so
 * the same definition appears wherever a term is used.
 */
import type { Tier } from '@/data/types';

export interface GlossaryEntry {
  term: string;
  short: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  tenant: {
    term: 'Tenant',
    short:
      'A customer organisation that has signed up to NousViz Cloud. One tenant can run multiple workspaces.',
  },
  workspace: {
    term: 'Workspace',
    short:
      'An isolated NousViz instance — its own database, droplet/namespace, plugins and users. A tenant typically has 1–5 of these (prod, staging, sandbox).',
  },
  fleet: {
    term: 'Fleet',
    short:
      'All infrastructure NousViz Cloud runs across every region. Droplets, K8s namespaces, capacity, version distribution. Operator view only.',
  },
  provisioning: {
    term: 'Provisioning',
    short:
      'Spinning up a new workspace. Runs the same 6-step installer used by self-hosted customers. Target: under 60 seconds end-to-end.',
  },
  provisioning_queue: {
    term: 'Provisioning queue',
    short:
      'The live queue of in-flight workspace creations. Each row is a job moving through stages: reserve infra → install → migrate DB → smoke test → live.',
  },
  mrr: {
    term: 'MRR',
    short:
      'Monthly Recurring Revenue — total monthly subscription income across all paying tenants. Excludes one-off charges and overages.',
  },
  arr: {
    term: 'ARR',
    short: 'Annualised Recurring Revenue — current MRR × 12.',
  },
  p95: {
    term: 'P95',
    short:
      '95th percentile. 95% of provisioning jobs finish faster than this number. Better than "average" because outliers don\'t skew it.',
  },
  mttr: {
    term: 'MTTR',
    short:
      'Mean Time To Resolve — average time from incident declared to incident resolved. P1 target: under 60 minutes.',
  },
  sla: {
    term: 'SLA',
    short:
      'Service Level Agreement — uptime % we commit to customers. Business tier: 99.9%. Enterprise: custom (typically 99.95%).',
  },
  slo: {
    term: 'SLO',
    short:
      'Service Level Objective — internal uptime target, usually one nine stricter than the SLA so we have headroom before we breach the commitment.',
  },
  error_budget: {
    term: 'Error budget',
    short:
      'How much downtime is allowed in a period before the SLA is breached. 99.9% SLA = 43 minutes of downtime per month. We burn it as incidents happen.',
  },
  rbac: {
    term: 'RBAC',
    short:
      'Role-Based Access Control. Internal staff (platform_admin / sre / support / billing / read_only) get different permissions to act on tenants.',
  },
  scim: {
    term: 'SCIM',
    short:
      'System for Cross-domain Identity Management — protocol that lets customer IdPs (Okta, Azure AD) automatically create / update / deactivate users in NousViz.',
  },
  churn_risk: {
    term: 'Churn risk',
    short:
      'A tenant flagged by usage signals (declining activity, support tickets, payment issues) as likely to cancel. Action before they leave.',
  },
  expansion: {
    term: 'Expansion',
    short:
      'A tenant whose usage is trending toward the next tier. Good candidates for upsell conversations.',
  },
  uptime_90d: {
    term: '90-day uptime',
    short:
      'One pixel-thin bar per day for the last 90 days. Green = clean. Amber = minor degradation. Red = major incident. Statuspage-style.',
  },
};

/** Tier definitions used by TierBadge tooltip. */
export const TIER_DETAILS: Record<Tier, {
  price: string;
  description: string;
  runtime: string;
  limits: string[];
  features: string[];
}> = {
  starter: {
    price: '$0/mo',
    description: 'Free tier for hobbyists, prototypes, and community users.',
    runtime: 'Shared K8s pool',
    limits: ['1 workspace', '100k rows/mo', '50k events/mo', '1 GB storage', '3 members'],
    features: ['Community plugins', 'Best-effort email support'],
  },
  team: {
    price: '$99/mo',
    description: 'Small teams running production data workloads.',
    runtime: 'Dedicated droplet per workspace',
    limits: ['5 workspaces', '2M rows/mo', '1M events/mo', '20 GB storage', '10 members'],
    features: ['Daily snapshot backups', 'Custom domain', 'Verified plugins', 'Priority email support'],
  },
  business: {
    price: '$499/mo',
    description: 'Organisations needing SSO, audit, and a real SLA.',
    runtime: 'Dedicated VM cluster (HA)',
    limits: ['Unlimited workspaces', '10M rows/mo', '5M events/mo', '100 GB storage', 'Unlimited members'],
    features: ['SSO (Google/Microsoft/Okta)', 'SCIM provisioning', 'Audit log + export', '99.9% uptime SLA', '7-day point-in-time restore'],
  },
  enterprise: {
    price: 'Custom',
    description: 'BYO-cloud single-tenant for regulated industries.',
    runtime: 'BYO-cloud (AWS / GCP / Azure / on-prem)',
    limits: ['No platform limits', 'Custom data residency'],
    features: ['Custom SLA + DPA', 'SOC 2 report', 'Dedicated success manager', 'On-premise option'],
  },
};

export function defineTerm(key: keyof typeof GLOSSARY) {
  return GLOSSARY[key];
}
